import { ChunkData, generateChunk } from './chunkgen';
import { CELL_SIZE, CHUNK_CELLS, CHUNK_SIZE, CHUNK_VERTS } from './grid';
import { Level } from './level';
import { normalize, vec } from './math';
import { ObstacleField } from './obstacles';
import { Obstacle, Terrain, TerrainSample } from './terrain';

export interface Chunk extends ChunkData { rut: Float32Array | null }

// Small-integer keys (under 2^30): a key past the Smi range is a boxed number, one allocation per lookup.
const key = (ci: number, cj: number) => (ci + 16384) * 32768 + (cj + 16384);

/**
 * Ground that riding changed is kept in pieces of PIECE x PIECE vertices (8 m): a ridden strip costs a few KB a metre
 * instead of the 8-13 MB a whole chunk's heights and ruts take (a long trail grew by ~1 MB/s and a phone ran out).
 * A piece is PIECE_AREA heights, followed by PIECE_AREA ruts when it carries them; rows are PIECE apart.
 */
const PIECE = 32;
const PIECES = Math.ceil(CHUNK_VERTS / PIECE); // per side; the last column and row hold one vertex line
const PIECE_AREA = PIECE * PIECE;
type Piece = Float32Array;

/** Copies piece `p` of the vertex grid into `piece[at ..]`, or back out of it into the grid (`back`). */
function movePiece(grid: Float32Array, p: number, piece: Piece, at: number, back: boolean): void {
  const i0 = (p % PIECES) * PIECE, j0 = Math.floor(p / PIECES) * PIECE;
  const w = Math.min(PIECE, CHUNK_VERTS - i0), h = Math.min(PIECE, CHUNK_VERTS - j0);
  for (let r = 0; r < h; r++) {
    const g = (j0 + r) * CHUNK_VERTS + i0, o = at + r * PIECE;
    if (back) grid.set(piece.subarray(o, o + w), g); else piece.set(grid.subarray(g, g + w), o);
  }
}

function pieceHasRut(rut: Float32Array, p: number): boolean {
  const i0 = (p % PIECES) * PIECE, j0 = Math.floor(p / PIECES) * PIECE;
  const w = Math.min(PIECE, CHUNK_VERTS - i0), h = Math.min(PIECE, CHUNK_VERTS - j0);
  for (let r = 0; r < h; r++) {
    const g = (j0 + r) * CHUNK_VERTS + i0;
    for (let k = 0; k < w; k++) if (rut[g + k] !== 0) return true;
  }
  return false;
}

/** The heights of piece `p` and, when `withRut`, its ruts. */
function copyPiece(c: Chunk, p: number, withRut: boolean): Piece {
  const piece = new Float32Array(withRut ? 2 * PIECE_AREA : PIECE_AREA);
  movePiece(c.field.heights, p, piece, 0, false);
  if (withRut && c.rut) movePiece(c.rut, p, piece, PIECE_AREA, false);
  return piece;
}

/** What a chunk keeps while it is not loaded: the pieces riding changed (the rest is generated again, bit-exact). */
interface Parked { pieces: Map<number, Piece> }

/**
 * The ground as a ride found it, kept copy-on-write: the first change to a piece of a chunk after the save copies
 * that piece (heights, and ruts when the chunk had ruts at the save), plus the knocked cones at the save. A replay
 * restores it so it rides the same ground the ride did.
 */
export class GroundSave {
  readonly chunks = new Map<number, { hadRut: boolean; pieces: Map<number, Piece> }>();
  constructor(readonly knocked: Uint8Array | null) {}
}

/**
 * Terrain streamed in 256 m chunks around a point. A chunk with ruts (or one a GroundSave holds) is parked when it
 * unloads, as the pieces it changed, and comes back as it was, so the ground under a ride never depends on when the
 * renderer streamed.
 */
/** sampleInto's probe scratch (no allocation per call). */
const SX = new Float64Array(5), SZ = new Float64Array(5), SH = new Float64Array(5);

export class ChunkedTerrain implements Terrain {
  readonly maxRutDepth = 0.15;
  misses = 0;
  generated = 0;
  generateMs = 0;
  version = 0;
  private readonly chunks = new Map<number, Chunk>();
  /** The loaded chunks as a plain array (kept with `chunks`), so the per-step paths iterate without allocating. */
  private readonly list: Chunk[] = [];
  private last: Chunk | null = null;

  private obstacleField: ObstacleField | null = null;
  private readonly saves: GroundSave[] = [];
  private readonly parked = new Map<number, Parked>();

  /** Trees, rocks, bales, cones, stakes and paddock props of this level, built lazily per chunk (src/sim/obstacles.ts). */
  get field(): ObstacleField {
    return (this.obstacleField ??= new ObstacleField(this.level, (x, z) => this.chunkAt(x, z), (x, z) => this.heightAt(x, z)));
  }

  constructor(readonly level: Level, readonly loadRadius = 160, readonly unloadRadius = 320) {
    if (unloadRadius <= loadRadius) throw new Error('unload radius must exceed load radius');
  }

  loaded(): Chunk[] { return [...this.chunks.values()]; }

  /** Chunk indices within `radius` of (x, z) that are not loaded yet, nearest first (for background generation). */
  missing(x: number, z: number, radius: number): [number, number][] {
    const out: [number, number, number][] = [];
    for (let ci = Math.floor((x - radius) / CHUNK_SIZE); ci <= Math.floor((x + radius) / CHUNK_SIZE); ci++) {
      for (let cj = Math.floor((z - radius) / CHUNK_SIZE); cj <= Math.floor((z + radius) / CHUNK_SIZE); cj++) {
        const d = this.rectDistance(ci, cj, x, z);
        if (d <= radius && !this.chunks.has(key(ci, cj))) out.push([ci, cj, d]);
      }
    }
    return out.sort((a, b) => a[2] - b[2]).map(([ci, cj]) => [ci, cj]);
  }

  /** Takes a chunk generated elsewhere (a worker running the same generateChunk). Ignored if already loaded. */
  adopt(data: ChunkData): void {
    if (this.chunks.has(key(data.ci, data.cj))) return;
    this.generated++;
    this.add(data);
  }

  /** Loads a generated chunk, with the ruts and the ground changes it was parked with. */
  private add(data: ChunkData): Chunk {
    const k = key(data.ci, data.cj), c: Chunk = { ...data, rut: null }, parked = this.parked.get(k);
    if (parked) {
      this.parked.delete(k);
      for (const [p, piece] of parked.pieces) {
        movePiece(c.field.heights, p, piece, 0, true);
        if (piece.length > PIECE_AREA) movePiece(c.rut ??= new Float32Array(CHUNK_VERTS * CHUNK_VERTS), p, piece, PIECE_AREA, true);
      }
    }
    this.version++;
    this.chunks.set(k, c);
    this.list.push(c);
    return c;
  }

  /** A chunk leaving memory keeps only the pieces its ruts changed; a chunk that changed nothing keeps nothing. */
  private park(c: Chunk): void {
    const k = key(c.ci, c.cj);
    if (!c.rut && !this.saves.some((s) => s.chunks.has(k))) return;
    const pieces = new Map<number, Piece>();
    if (c.rut) {
      for (let p = 0; p < PIECES * PIECES; p++) if (pieceHasRut(c.rut, p)) pieces.set(p, copyPiece(c, p, true));
    }
    this.parked.set(k, { pieces });
  }

  /** Starts a save point of the ground (ruts, knocked cones); release it when it is no longer needed. */
  saveGround(): GroundSave {
    const s = new GroundSave(this.obstacleField?.knocked.slice() ?? null);
    this.saves.push(s);
    return s;
  }

  releaseGround(s: GroundSave | null | undefined): void {
    const i = s ? this.saves.indexOf(s) : -1;
    if (i >= 0) this.saves.splice(i, 1);
  }

  /** Puts the ground back as it was at the save (the save stays valid: restore it again later if needed). */
  restoreGround(s: GroundSave): void {
    for (const [k, e] of s.chunks) {
      const parked = this.parked.get(k);
      if (parked && this.saves.length === 1 && this.saves[0] === s) {
        // Nobody else needs the ground as it is now: patch what the chunk keeps (a piece without ruts is generated again).
        for (const [p, piece] of e.pieces) {
          if (e.hadRut) parked.pieces.set(p, piece.slice()); else parked.pieces.delete(p);
        }
        continue;
      }
      const c = this.chunks.get(k) ?? this.load(Math.floor(k / 32768) - 16384, (k % 32768) - 16384);
      for (const p of e.pieces.keys()) this.touchPiece(c, k, p);
      for (const [p, piece] of e.pieces) {
        movePiece(c.field.heights, p, piece, 0, true);
        if (e.hadRut) movePiece(c.rut ??= new Float32Array(CHUNK_VERTS * CHUNK_VERTS), p, piece, PIECE_AREA, true);
      }
      if (!e.hadRut) c.rut = null;
    }
    const f = this.obstacleField;
    if (f) { f.knocked.fill(0); if (s.knocked) f.knocked.set(s.knocked); }
  }

  /** Before everything in a chunk that carries ruts changes: every open save that has not seen those pieces copies them. */
  touchChunk(c: Chunk): void {
    if (!c.rut || !this.saves.length) return;
    const k = key(c.ci, c.cj);
    for (let p = 0; p < PIECES * PIECES; p++) if (pieceHasRut(c.rut, p)) this.touchPiece(c, k, p);
  }

  /** Before piece `p` of chunk `c` changes: every open save that has not seen it yet copies it. */
  private touchPiece(c: Chunk, k: number, p: number): void {
    for (const s of this.saves) {
      let e = s.chunks.get(k);
      if (!e) s.chunks.set(k, e = { hadRut: c.rut !== null, pieces: new Map() });
      if (!e.pieces.has(p)) e.pieces.set(p, copyPiece(c, p, e.hadRut));
    }
  }

  /** Before the vertices (i0..i1, j0..j1) of chunk `c` change. */
  private touch(c: Chunk, i0: number, i1: number, j0: number, j1: number): void {
    const k = key(c.ci, c.cj);
    for (let pj = Math.floor(j0 / PIECE), pj1 = Math.floor(j1 / PIECE); pj <= pj1; pj++) {
      for (let pi = Math.floor(i0 / PIECE), pi1 = Math.floor(i1 / PIECE); pi <= pi1; pi++) this.touchPiece(c, k, pj * PIECES + pi);
    }
  }

  private load(ci: number, cj: number): Chunk {
    const t0 = performance.now();
    const data = generateChunk(this.level, ci, cj);
    this.generateMs += performance.now() - t0;
    this.generated++;
    return this.add(data);
  }

  private rectDistance(ci: number, cj: number, x: number, z: number): number {
    const ox = ci * CHUNK_SIZE, oz = cj * CHUNK_SIZE;
    const dx = Math.max(ox - x, 0, x - ox - CHUNK_SIZE);
    const dz = Math.max(oz - z, 0, z - oz - CHUNK_SIZE);
    return Math.sqrt(dx * dx + dz * dz);
  }

  update(x: number, z: number): void {
    const r = this.loadRadius;
    for (let ci = Math.floor((x - r) / CHUNK_SIZE); ci <= Math.floor((x + r) / CHUNK_SIZE); ci++) {
      for (let cj = Math.floor((z - r) / CHUNK_SIZE); cj <= Math.floor((z + r) / CHUNK_SIZE); cj++) {
        if (!this.chunks.has(key(ci, cj)) && this.rectDistance(ci, cj, x, z) <= r) this.load(ci, cj);
      }
    }
    for (let i = this.list.length - 1; i >= 0; i--) {
      const c = this.list[i]!;
      if (this.rectDistance(c.ci, c.cj, x, z) > this.unloadRadius) {
        this.chunks.delete(key(c.ci, c.cj));
        this.list.splice(i, 1);
        this.park(c);
        if (this.last === c) this.last = null;
        this.version++;
      }
    }
  }

  chunkAt(x: number, z: number): Chunk {
    return this.chunkOf(Math.floor(x / CHUNK_SIZE), Math.floor(z / CHUNK_SIZE));
  }

  /** Chunk (ci, cj); integer arguments, so hot callers pass no boxed doubles. */
  private chunkOf(ci: number, cj: number): Chunk {
    const last = this.last;
    if (last && last.ci === ci && last.cj === cj) return last;
    let c = this.chunks.get(key(ci, cj));
    if (!c) { this.misses++; c = this.load(ci, cj); }
    this.last = c;
    return c;
  }

  heightAt(x: number, z: number): number { return this.chunkAt(x, z).field.heightAt(x, z); }

  sample(x: number, z: number): TerrainSample {
    const e = CELL_SIZE;
    const normal = normalize(vec(this.heightAt(x - e, z) - this.heightAt(x + e, z), 2 * e, this.heightAt(x, z - e) - this.heightAt(x, z + e)));
    const c = this.chunkAt(x, z);
    return { height: c.field.heightAt(x, z), normal, surface: c.field.surfaceAt(x, z) };
  }

  heightsInto(xs: Float64Array, zs: Float64Array, n: number, out: Float64Array): void {
    for (let i = 0; i < n; i++) this.chunkOf(Math.floor(xs[i]! / CHUNK_SIZE), Math.floor(zs[i]! / CHUNK_SIZE)).field.heightIdx(xs, zs, i, out);
  }

  sampleInto(x: number, z: number, out: TerrainSample): void {
    const e = CELL_SIZE;
    const xs = SX, zs = SZ, h = SH;
    xs[0] = x - e; zs[0] = z; xs[1] = x + e; zs[1] = z; xs[2] = x; zs[2] = z - e; xs[3] = x; zs[3] = z + e; xs[4] = x; zs[4] = z;
    this.heightsInto(xs, zs, 5, h);
    const nx = h[0]! - h[1]!, ny = 2 * e, nz = h[2]! - h[3]!;
    const l = Math.sqrt(nx * nx + ny * ny + nz * nz);
    out.normal.x = nx / l; out.normal.y = ny / l; out.normal.z = nz / l;
    out.height = h[4]!;
    out.surface = this.chunkAt(x, z).field.surfaceAt(x, z);
  }

  obstacles(x: number, z: number, radius: number): Obstacle[] {
    const out: Obstacle[] = [];
    const forest = this.level.def.forest;
    if (!forest) return out;
    const sp = forest.spacing, reach = radius + forest.maxRadius + sp;
    for (let gb = Math.floor((z - reach) / sp); gb <= Math.floor((z + reach) / sp); gb++) {
      for (let ga = Math.floor((x - reach) / sp); ga <= Math.floor((x + reach) / sp); ga++) {
        const c = this.chunkAt((ga + 0.5) * sp, (gb + 0.5) * sp);
        const t = c.trees!;
        const k = (gb - c.cj * t.perSide) * t.perSide + (ga - c.ci * t.perSide);
        const r = t.radius[k]!;
        if (r > 0 && Math.sqrt((t.xs[k]! - x) * (t.xs[k]! - x) + (t.zs[k]! - z) * (t.zs[k]! - z)) < radius + r) out.push({ x: t.xs[k]!, z: t.zs[k]!, radius: r });
      }
    }
    return out;
  }

  /** Rut depth at (x, z), bilinear between grid vertices; 0 where nothing has been ridden. */
  rutDepth(x: number, z: number): number {
    const c = this.chunkAt(x, z);
    const rut = c.rut;
    if (!rut) return 0;
    const fx = Math.min(CHUNK_CELLS, Math.max(0, (x - c.ci * CHUNK_SIZE) / CELL_SIZE));
    const fz = Math.min(CHUNK_CELLS, Math.max(0, (z - c.cj * CHUNK_SIZE) / CELL_SIZE));
    const i = Math.min(Math.floor(fx), CHUNK_CELLS - 1), j = Math.min(Math.floor(fz), CHUNK_CELLS - 1);
    const tx = fx - i, tz = fz - j, v = j * CHUNK_VERTS + i;
    return (rut[v]! * (1 - tx) + rut[v + 1]! * tx) * (1 - tz) + (rut[v + CHUNK_VERTS]! * (1 - tx) + rut[v + CHUNK_VERTS + 1]! * tx) * tz;
  }

  /** Lowers loaded ground by up to `depth` at (x, z), fading to 0 at `radius`; total rut depth is capped. */
  deform(x: number, z: number, depth: number, radius: number): void {
    for (let n = 0; n < this.list.length; n++) {
      const c = this.list[n]!;
      const ox = c.ci * CHUNK_SIZE, oz = c.cj * CHUNK_SIZE;
      const i0 = Math.max(0, Math.ceil((x - radius - ox) / CELL_SIZE)), i1 = Math.min(CHUNK_CELLS, Math.floor((x + radius - ox) / CELL_SIZE));
      const j0 = Math.max(0, Math.ceil((z - radius - oz) / CELL_SIZE)), j1 = Math.min(CHUNK_CELLS, Math.floor((z + radius - oz) / CELL_SIZE));
      if (i0 > i1 || j0 > j1) continue;
      if (this.saves.length) this.touch(c, i0, i1, j0, j1);
      const rut = (c.rut ??= new Float32Array(CHUNK_VERTS * CHUNK_VERTS));
      const h = c.field.heights;
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const dist = Math.sqrt((ox + i * CELL_SIZE - x) * (ox + i * CELL_SIZE - x) + (oz + j * CELL_SIZE - z) * (oz + j * CELL_SIZE - z));
          if (dist >= radius) continue;
          const v = j * CHUNK_VERTS + i;
          const next = Math.min(this.maxRutDepth, rut[v]! + depth * (1 - dist / radius) ** 2);
          h[v] = h[v]! - (next - rut[v]!);
          rut[v] = next;
        }
      }
    }
  }
}
